
import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Users, MessageSquare, Mail, UserPlus } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

// Sample staff data
const staffs = [
  {
    id: 1,
    name: 'Sarah Johnson',
    role: 'Community Manager',
    avatar: '/placeholder.svg',
    department: 'Management',
    email: 'sarah.j@ahavex.com',
    joined: 'Jan 2022',
  },
  {
    id: 2,
    name: 'Michael Chen',
    role: 'Content Specialist',
    avatar: '/placeholder.svg',
    department: 'Marketing',
    email: 'michael.c@ahavex.com',
    joined: 'Mar 2022',
  },
  {
    id: 3,
    name: 'Olivia Patel',
    role: 'Business Advisor',
    avatar: '/placeholder.svg',
    department: 'Advisory',
    email: 'olivia.p@ahavex.com',
    joined: 'Apr 2022',
  },
  {
    id: 4,
    name: 'David Okonkwo',
    role: 'Events Coordinator',
    avatar: '/placeholder.svg',
    department: 'Events',
    email: 'david.o@ahavex.com',
    joined: 'Jun 2022',
  },
  {
    id: 5,
    name: 'Aisha Mohammed',
    role: 'Member Relations',
    avatar: '/placeholder.svg',
    department: 'Support',
    email: 'aisha.m@ahavex.com',
    joined: 'Aug 2022',
  },
  {
    id: 6,
    name: 'Robert Kim',
    role: 'Technical Support',
    avatar: '/placeholder.svg',
    department: 'IT',
    email: 'robert.k@ahavex.com',
    joined: 'Oct 2022',
  },
];

const StaffsTab: React.FC = () => {
  const isMobile = useIsMobile();
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="font-bold text-base">Ahavex Community Staff</h2>
        <Button className="bg-ahavex-blue text-xs">
          <Users className="mr-2 h-3 w-3" />
          View All Staff
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {staffs.map(staff => (
          <motion.div 
            key={staff.id} 
            initial={{opacity: 0, y: 20}} 
            animate={{opacity: 1, y: 0}} 
            transition={{duration: 0.5}}
          >
            <Card className="h-full">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={staff.avatar} alt={staff.name} />
                      <AvatarFallback>{staff.name.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-sm">{staff.name}</p>
                      <p className="text-xs text-gray-500">{staff.joined}</p>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <h3 className="text-sm font-semibold mb-1">{staff.role}</h3>
                <p className="text-xs text-gray-600 mb-2">{staff.email}</p>
                <div className="flex flex-wrap gap-1 mb-1">
                  <Badge variant="outline" className="bg-blue-50 text-ahavex-blue text-xs px-1.5 py-0">
                    {staff.department}
                  </Badge>
                </div>
              </CardContent>
              <CardFooter className="pt-1 flex justify-between border-t">
                <div className="flex items-center gap-2">
                  {isMobile ? (
                    <Button variant="outline" size="sm" className="h-6 p-1 text-xs">
                      <Mail className="h-3 w-3" />
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" className="h-6 text-xs">
                      <Mail className="h-3 w-3 mr-1" />
                      Message
                    </Button>
                  )}
                  
                  {isMobile ? (
                    <Button variant="outline" size="sm" className="h-6 p-1 text-xs">
                      <UserPlus className="h-3 w-3" />
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" className="h-6 text-xs">
                      <UserPlus className="h-3 w-3 mr-1" />
                      Connect
                    </Button>
                  )}
                </div>
              </CardFooter>
            </Card>
          </motion.div>
        ))}
      </div>
      
      <div className="flex justify-center">
        <Button variant="outline" className="mt-2 text-xs">
          Load More Staff
        </Button>
      </div>
    </div>
  );
};

export default StaffsTab;
